/**
 * Platform Analytics API client.
 *
 * Helix-only endpoints for user engagement, feature stickiness,
 * company activity, and platform health metrics.
 */
import api from './client';

export interface PlatformOverview {
  period: { date_from: string; date_to: string };
  kpis: {
    dau: number;
    wau: number;
    mau: number;
    total_requests: number;
    active_companies: number;
    active_customer_companies: number;
    active_supplier_companies: number;
    stickiness_dau_mau: number;
    stickiness_wau_mau: number;
  };
  /** Activity kept out of the KPIs above, reported so nothing disappears. */
  excluded: {
    staff_requests: number;
    staff_users: number;
    login_as_requests: number;
    login_as_users: number;
    background_polls: number;
  };
}

export interface FeatureUsageItem {
  feature: string;
  requests: number;
  unique_users: number;
  unique_companies: number;
  percentage: number;
}

export interface CompanyEngagement {
  company_id: number;
  company_name: string;
  company_type: string;
  active_users: number;
  total_requests: number;
  /** Work areas used (notifications, news and settings excluded). */
  unique_features: number;
  /** People at the company with an active login. */
  logins: number;
  engagement_score: number;
  components: UsageScoreComponents;
  /** Score over the same-length window just before; null if not active then. */
  previous_score: number | null;
  change: number | null;
  status: UsageStatus;
}

export type UsageStatus = 'healthy' | 'watch' | 'at_risk';

/** Each 0-1; score = 100 x (0.4 frequency + 0.3 reach + 0.3 breadth). */
export interface UsageScoreComponents {
  frequency: number;
  reach: number;
  breadth: number;
}

/** Account score components: reach is the mean of the two reach shares. */
export interface AccountScoreComponents extends UsageScoreComponents {
  customer_reach: number;
  supplier_reach: number;
}

export interface AccountUsageWindow {
  date_from: string;
  date_to: string;
  score: number;
  components: AccountScoreComponents;
  inputs: {
    customer_people_active: number;
    customer_logins: number;
    customer_active_days_per_person: number;
    working_days: number;
    work_areas_used: number;
    work_areas_live: number;
    supplier_companies_active: number;
    supplier_companies_with_login: number;
  };
}

export interface AccountUsageScore {
  current: AccountUsageWindow;
  previous: AccountUsageWindow;
  /** Points vs the previous 30 days. */
  change: number;
  status: UsageStatus;
  weights: { frequency: number; reach: number; breadth: number };
  thresholds: { healthy: number; watch: number; drop_alert: number };
}

export interface UserActivityItem {
  user_id: number;
  email: string;
  name: string;
  company: string;
  company_type: string;
  total_requests: number;
  unique_features: number;
  active_days: number;
}

export interface TrendPoint {
  date: string;
  requests: number;
  unique_users: number;
  unique_companies: number;
}

export interface ProcessAdoptionItem {
  feature: string;
  companies_using: number;
  total_companies: number;
  adoption_percentage: number;
  total_requests: number;
  status: 'high' | 'medium' | 'low';
}

interface DateRangeParams {
  date_from?: string;
  date_to?: string;
  company_type?: string;
}

export const platformAnalyticsAPI = {
  getOverview: (params?: DateRangeParams) =>
    api.get<PlatformOverview>('/analytics/platform/overview/', { params }),

  getFeatureUsage: (params?: DateRangeParams) =>
    api.get<{ features: FeatureUsageItem[]; total_requests: number }>(
      '/analytics/platform/feature-usage/', { params }
    ),

  getCompanyEngagement: (params?: DateRangeParams & { limit?: number }) =>
    api.get<{ companies: CompanyEngagement[] }>(
      '/analytics/platform/company-engagement/', { params }
    ),

  getUserActivity: (params?: DateRangeParams & { company_id?: number; limit?: number }) =>
    api.get<{ users: UserActivityItem[] }>(
      '/analytics/platform/user-activity/', { params }
    ),

  getActivityTrend: (params?: DateRangeParams) =>
    api.get<{ trend: TrendPoint[] }>(
      '/analytics/platform/activity-trend/', { params }
    ),

  getUsageScore: () =>
    api.get<AccountUsageScore>('/analytics/platform/usage-score/'),

  getProcessAdoption: (params?: DateRangeParams) =>
    api.get<{ adoption: ProcessAdoptionItem[]; total_active_companies: number }>(
      '/analytics/platform/process-adoption/', { params }
    ),
};
